import { useState } from 'react';
import axios from 'axios';
import { X, Eye, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker, TimePicker } from '@/components/ui/native-datetime';
import { MultiSelect } from '@/components/ui/multi-select';
import { toast } from 'sonner';
import { formatSubmitError } from '@/utils/formErrors';
import { useNavigate } from 'react-router-dom';
import { EMPLOYEE_NAMES as FALLBACK_NAMES } from '@/lib/workCalculations';
import useDirectory from '@/hooks/useDirectory';
import useUsedDates from '@/hooks/useUsedDates';
import { todayISO, Req, Opt, labelCls, fieldInput, fieldTextarea, fieldTrigger, btnPrimary, btnSecondary } from '@/components/forms/formKit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  form_type: 'work_declaration',
  employee_names: [],
  employee_code: '',
  id_card: '',
  department: '',
  occurrence_date: todayISO(),
  actual_start_time: '',
  actual_end_time: '',
  reason_type: '',
  comments: '',
};

export default function WorkDeclarationFormModal({ onClose, initialData, submissionId, embedded = false }) {
  const navigate = useNavigate();
  const { employees, employeeNames, departmentNames } = useDirectory();
  const empOptions = employeeNames.length ? employeeNames : FALLBACK_NAMES;
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => {
    if (!initialData) return { ...EMPTY };
    const d = { ...EMPTY, ...initialData };
    if (!Array.isArray(d.employee_names) || d.employee_names.length === 0) {
      if (typeof initialData.employee_name === 'string' && initialData.employee_name) {
        d.employee_names = initialData.employee_name.split('、').map((s) => s.trim()).filter(Boolean);
      } else {
        d.employee_names = [];
      }
    }
    return d;
  });
  const isEdit = !!submissionId;
  const usedDates = useUsedDates('work_declaration', formData.employee_names, submissionId);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'employee_names' && Array.isArray(value) && value.length > 0) {
        const first = employees.find((e) => e.name === value[0]);
        if (first) {
          updated.employee_code = first.code || '';
          updated.id_card = first.id_card || '';
          if (first.department_name && !prev.department) updated.department = first.department_name;
        }
      }
      if (field === 'employee_names' && Array.isArray(value) && value.length === 0) {
        updated.employee_code = '';
        updated.id_card = '';
      }
      if (field === 'department') {
        // Drop names not in this dept
        const next = (prev.employee_names || []).filter((n) => {
          const emp = employees.find((e) => e.name === n);
          return emp && emp.department_name === value;
        });
        updated.employee_names = next;
        if (next.length === 0) { updated.employee_code = ''; updated.id_card = ''; }
      }
      return updated;
    });
  };

  const filteredEmpOptions = formData.department
    ? employees.filter((e) => e.department_name === formData.department).map((e) => e.name)
    : empOptions;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        employee_name: formData.employee_names.join('、'),
      };
      if (isEdit) {
        await axios.put(`${API}/forms/submissions/${submissionId}`, { form_data: submitData });
        toast.success('申請を更新しました');
        if (onClose) onClose();
        setTimeout(() => navigate(`/submission/${submissionId}`), 300);
      } else {
        await axios.post(`${API}/forms/submit`, submitData);
        toast.success('登録しました — 履歴に追加されました');
        setFormData({ ...EMPTY });
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(formatSubmitError(error, isEdit));
    } finally {
      setSubmitting(false);
    }
  };

  const body = (
    <>
      <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-5 flex items-center justify-between z-20">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">
          勤務時間申告書{isEdit && <span className="ml-2 text-sm font-normal text-zinc-500">— 編集</span>}
        </h2>
        {onClose && (
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-500 hover:text-zinc-900">
            <X size={24} />
          </button>
        )}
      </div>

        {!showPreview ? (
          <div className="p-6 sm:p-8 space-y-6">
            <div>
              <Label htmlFor="department" className={labelCls}>所属部署<Req /></Label>
              {departmentNames.length > 0 ? (
                <Select value={formData.department} onValueChange={(val) => handleChange('department', val)}>
                  <SelectTrigger data-testid="select-department" className={`mt-1.5 ${fieldTrigger}`}>
                    <SelectValue placeholder="所属部署を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {departmentNames.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className={`mt-1.5 ${fieldInput}`}
                  required
                />
              )}
            </div>
            <div>
              <Label className={labelCls}>氏名<Req /><span className="ml-2 text-xs font-normal text-zinc-500">複数選択可</span></Label>
              <MultiSelect
                options={filteredEmpOptions}
                selected={formData.employee_names}
                onChange={(names) => handleChange('employee_names', names)}
                placeholder={formData.department ? '氏名を選択してください（複数可）' : 'まず所属部署を選択してください'}
                testId="multi-select-employee-names"
                disabled={!formData.department && departmentNames.length > 0}
              />
              {formData.department && filteredEmpOptions.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">この部署に登録された社員がいません。</p>
              )}
            </div>
            <div>
              <Label htmlFor="occurrence_date" className={labelCls}>発生日<Req /></Label>
              <DatePicker
                id="occurrence_date"
                value={formData.occurrence_date}
                onChange={(v) => handleChange('occurrence_date', v)}
                disabledDates={usedDates}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="actual_start_time" className={labelCls}>実際の開始時刻<Req /></Label>
                <TimePicker
                  id="actual_start_time"
                  value={formData.actual_start_time}
                  onChange={(v) => handleChange('actual_start_time', v)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="actual_end_time" className={labelCls}>実際の終了時刻<Req /></Label>
                <TimePicker
                  id="actual_end_time"
                  value={formData.actual_end_time}
                  onChange={(v) => handleChange('actual_end_time', v)}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reason_type" className={labelCls}>勤怠データ不備理由<Req /></Label>
              <Select value={formData.reason_type} onValueChange={(val) => handleChange('reason_type', val)}>
                <SelectTrigger data-testid="select-reason-type" className={`mt-1.5 ${fieldTrigger}`}>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="スキャン忘れ">スキャン忘れ</SelectItem>
                  <SelectItem value="ボタン切り替えミス">出退勤ボタン切り替えミス</SelectItem>
                  <SelectItem value="その他">その他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="comments" className={labelCls}>詳細・コメント<Opt /></Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => handleChange('comments', e.target.value)}
                className={`mt-1.5 ${fieldTextarea}`}
                placeholder="追加情報があれば記載してください"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={onClose} className="border-zinc-300">
                キャンセル
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                disabled={
                  !formData.employee_names?.length ||
                  !formData.department ||
                  !formData.occurrence_date ||
                  !formData.actual_start_time ||
                  !formData.actual_end_time ||
                  !formData.reason_type
                }
                className="bg-zinc-800 hover:bg-zinc-900 shadow-sm hover:shadow-md transition-all"
              >
                <Eye size={18} className="mr-2" />
                プレビュー
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="bg-gradient-to-br from-zinc-50 to-white rounded-lg border-2 border-dashed border-zinc-200 p-6 sm:p-8 mb-6 shadow-inner">
              <h3 className="text-lg font-bold mb-4 border-b border-zinc-300 pb-2">申請内容の確認</h3>
              <dl className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">氏名:</dt>
                  <dd className="col-span-2 text-zinc-900"><span data-no-translate>{formData.employee_name}</span></dd>
                </div>
                {formData.employee_code && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="font-medium text-zinc-600">個人コード:</dt>
                    <dd className="col-span-2 text-zinc-900">{formData.employee_code}</dd>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">所属部署:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.department}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">発生日:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.occurrence_date}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">実際の勤務時間:</dt>
                  <dd className="col-span-2 text-zinc-900">
                    {formData.actual_start_time} ~ {formData.actual_end_time}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">不備理由:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.reason_type}</dd>
                </div>
                {formData.comments && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="font-medium text-zinc-600">コメント:</dt>
                    <dd className="col-span-2 text-zinc-900 whitespace-pre-wrap">{formData.comments}</dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="border-zinc-300">
                戻る
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="form-submit-btn" className="bg-teal-700 hover:bg-teal-800 shadow-sm hover:shadow-md transition-all">
                <PaperPlaneTilt size={18} className="mr-2" />
                {submitting ? (isEdit ? '更新中…' : '登録中…') : (isEdit ? '更新する' : '登録する')}
              </Button>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 w-full overflow-hidden" data-testid="work-declaration-edit">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-zinc-900/70 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl border border-zinc-200 w-full max-w-2xl max-h-[calc(100vh-7rem)] overflow-y-auto mt-20 sm:mt-24 mx-4 mb-4">
        {body}
      </div>
    </div>
  );
}
